import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CardChrome } from './_CardChrome';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { acceptInvite, ExternalApiError } from '../../services/api/externalAuth';
import { useExternalAuth } from '../../services/externalAuthContext';
import { passwordSetSchema, type PasswordSetInput } from './_schemas';

/**
 * Accept-invite page.
 *
 * Mirrors ResetPasswordPage's token-bearing shape but: (a) submits with field
 * `password` (server contract — invite uses `password`, reset uses `newPassword`),
 * (b) expects the backend to issue the session cookie on success, (c) calls
 * useExternalAuth().refresh() to populate provider state, and (d) navigates to
 * /external/projects.
 *
 * Token-missing path links to mailto: (no self-serve "request invite" exists in
 * v4.0 — admin-invite-only).
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { refresh } = useExternalAuth();
  const headingRef = useRef<HTMLDivElement>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  useEffect(() => {
    if (!token && headingRef.current) headingRef.current.focus();
  }, [token]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordSetInput>({
    resolver: zodResolver(passwordSetSchema),
    mode: 'onSubmit',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  if (!token) {
    return (
      <CardChrome>
        <div ref={headingRef} tabIndex={-1}>
          <h1 className="text-lg font-semibold text-gray-900 text-center mb-4">
            Link unavailable
          </h1>
          <p className="text-sm text-gray-600 text-center mb-4">
            This link is invalid or has expired.
          </p>
          <a
            href="mailto:admin@topmonks.com"
            className="block text-center text-sm text-primary-600 hover:text-primary-700"
          >
            Contact the team to request a new invite
          </a>
        </div>
      </CardChrome>
    );
  }

  const onSubmit = async (values: PasswordSetInput) => {
    setBannerError(null);
    try {
      await acceptInvite({ token, password: values.newPassword });
      // Backend issued the session cookie. Re-fetch /me so provider state is populated.
      await refresh();
      navigate('/external/projects', { replace: true });
    } catch (err) {
      if (err instanceof ExternalApiError && err.errorCode === 'INVALID_TOKEN') {
        setBannerError(
          'This invite link is invalid or has expired. Contact the team to request a new invite.',
        );
      } else {
        setBannerError('Something went wrong, please try again.');
      }
    }
  };

  return (
    <CardChrome>
      {bannerError && (
        <div
          role="alert"
          className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4"
        >
          {bannerError}
        </div>
      )}
      <p className="text-sm text-gray-500 text-center mb-6">
        Choose a password to activate your account.
      </p>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input
          label="Set your password"
          type="password"
          autoComplete="new-password"
          error={errors.newPassword?.message}
          {...register('newPassword')}
        />
        <div className="mt-4">
          <Input
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />
        </div>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          isLoading={isSubmitting}
          className="w-full mt-6"
        >
          Accept invite
        </Button>
      </form>
    </CardChrome>
  );
}
